import React from 'react';
import { useState, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import axonSprite from '@rsos/components/capstone/common/Map/assets/axon_sprite_2x.png';
import {
  hidePopover,
  showPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import { default as IconLayer } from '@rsos/components/capstone/common/Map/useDeckGL/RSOSLayers/RSOSIconLayer';
import TrailingPathLayer from '@rsos/components/capstone/common/Map/useDeckGL/RSOSLayers/TrailingPathLayer';
import layers from '@rsos/components/capstone/common/Map/useDeckGL/storeLayers';
import {
  AXON_MAP_CARD,
  setCardsToRender,
  setMapCardData,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  trackAxonDataset,
  trackMarkerClick,
  trackMarkerHover,
} from './axonTracking';
import createCycleStorage from './createCycleStorage';

// NOTE The following sizes were worked on together with Design.
// Remember to re-adjust if the axonSprite image dimensions change.
const MARKER_SIZES = {
  sizeScale: 10, // this is the factor to scale the icon.
  sizeMinPixels: 30, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 40, // when zoomed in, the max height of the icon.
};

export const LAYERS = {
  AXON_BODY_3: 'axon_police_tracking',
  AXON_FLEET_3: 'axon_fleet_tracking', // to be implemented
};

const DEVICES = {
  AXON_BODY: 'axon body',
  AXON_FLEET: 'axon fleet', // to be implemented
};

const LAYER_IDS = {
  HISTORICAL_TAILS_BORDER: 'axon-historical-tails-border',
  HISTORICAL_TAILS_BG: 'axon-historical-tails-bg',
  HISTORICAL_TAILS_DASHED: 'axon-historical-tails-dashed',
  AXON_MAIN_LAYER: 'axon-main-layer',
  AXON_ACTIVE_LAYER: 'axon-active-layer',
};

// The max number of historical positions to show as tailing data per marker.
const MAX_HISTORICAL_POSITIONS = 5;

export const positionStorage = createCycleStorage({
  maxSize: MAX_HISTORICAL_POSITIONS,
});

const ICON_MAPPING = {
  body3ReadyDefault: { x: 0, y: 77, width: 77, height: 72, mask: false },
  body3ReadyActive: { x: 77, y: 77, width: 77, height: 72, mask: false },
  body3RecordingDefault: { x: 0, y: 0, width: 77, height: 72, mask: false },
  body3RecordingActive: { x: 77, y: 0, width: 77, height: 72, mask: false },
  body3InactiveDefault: { x: 0, y: 144, width: 77, height: 72, mask: false },
  body3InactiveActive: { x: 77, y: 144, width: 77, height: 72, mask: false },
  fleet3ReadyDefault: { x: 0, y: 287, width: 77, height: 72, mask: false },
  fleet3ReadyActive: { x: 77, y: 287, width: 77, height: 72, mask: false },
  fleet3RecordingDefault: { x: 0, y: 216, width: 77, height: 72, mask: false },
  fleet3RecordingActive: { x: 77, y: 216, width: 77, height: 72, mask: false },
  fleet3InactiveDefault: { x: 0, y: 357, width: 77, height: 72, mask: false },
  fleet3InactiveActive: { x: 77, y: 357, width: 77, height: 72, mask: false },
};

export const getLatestDataset = datasets => {
  if (!datasets) {
    return;
  }
  const keys = Object.keys(datasets)
    .map(k => parseInt(k, 10))
    .sort((a, b) => a - b);
  const lastKey = [...keys].pop();
  return datasets[lastKey];
};

/**
 * Select the latest dataset from the given redux state. To be used with
 * @usage const dataset = useSelector(selectLatestDataset);
 * @param {Redux.state} state
 */
export const selectLatestDataset = state => {
  const datasets = state.axon.datasets;
  return getLatestDataset(datasets);
};

const useAxonGL = ({
  activeMarkerId,
  overlay,
  onClick,
  onHover,
  positions = [],
  isMapIconDisabled,
}) => {
  const dataset = useSelector(selectLatestDataset);
  const visible = useSelector(state => state.axon.visible);

  // Draw trailing paths
  useEffect(() => {
    if (positions.length) {
      const data = positions.reduce((result, o) => {
        positionStorage.clear();
        positionStorage.load(o.data);
        const parsed = positionStorage.all();
        if (Array.isArray(parsed)) {
          result.push(parsed.map(o => o.coordinates));
        }
        return result;
      }, []);

      layers.save(
        new TrailingPathLayer({
          id: `${LAYERS.AXON_BODY_3}-trailing-path-layer`,
          data,
          visible,
          getPath: d => d,
        }),
      );
    }
  }, [positions, visible]);

  // Draw markers
  useEffect(() => {
    if (overlay && dataset) {
      trackAxonDataset(LAYERS.AXON_BODY_3, dataset, visible);

      layers.save(
        new IconLayer({
          id: LAYER_IDS.AXON_MAIN_LAYER,
          data: Object.values(dataset),
          getPosition: d => {
            return d?.shape?.features[0]?.geometry?.coordinates;
          },
          pickable: !isMapIconDisabled,
          autoHighlight: true,
          highlightColor: [255, 255, 255, 60],
          iconAtlas: axonSprite,
          iconMapping: ICON_MAPPING,
          sizeUnits: 'meters',
          ...MARKER_SIZES,
          getIcon: d => {
            const isAxonBodyDevice = `${
              d?.attributes?.device?.device_model || ''
            }`
              .toLowerCase()
              .includes(DEVICES.AXON_BODY);
            if (isAxonBodyDevice) {
              // First handle the currently viewed marker, if any
              if (d.external_id === activeMarkerId) {
                if (d.inactive) {
                  return 'body3InactiveActive';
                }
                if (d.attributes.device.status === 'Recording') {
                  return 'body3RecordingActive';
                }
                return 'body3ReadyActive';
              }

              // Then handle the "default" states
              if (d.inactive) {
                return 'body3InactiveDefault';
              }
              if (d.attributes.device.status === 'Recording') {
                return 'body3RecordingDefault';
              }
              return 'body3ReadyDefault';
            }
          },

          onHover: info => {
            onHover(info, LAYERS.AXON_BODY_3);
          },

          onClick: info => {
            onClick(info, LAYERS.AXON_BODY_3);
          },

          visible,

          updateTriggers: {
            getIcon: [activeMarkerId],
          },
        }),
      );

      overlay.setLayers([...layers.getAll()]);
    }
  }, [
    activeMarkerId,
    dataset,
    isMapIconDisabled,
    onClick,
    onHover,
    overlay,
    visible,
  ]);
};

export const AxonGLNext = React.memo(({ overlay, isMapIconDisabled }) => {
  const dispatch = useDispatch();
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [hoveredMarkerInfo, setHoveredMarkerInfo] = useState(null);

  const positions = useSelector(state => {
    return state.axon.positions;
  }, shallowEqual);

  const layerData = useSelector(
    state => state.schemas.byId[LAYERS.AXON_BODY_3],
  );

  const openedMarkerId = useSelector(state => {
    return state.mapCard?.mapCardData?.spatialObject?.external_id;
  });

  // Keep track of the map card, when it closes, clear the active marker.
  useEffect(() => {
    if (!openedMarkerId) {
      setActiveMarkerId(null);
    }
  }, [openedMarkerId]);

  // Show popover when hovering over a marker or when position changes.
  useEffect(() => {
    if (hoveredMarkerInfo) {
      // While hovered, find the latest position for this marker.
      const spatialObjectPositions =
        positions[hoveredMarkerInfo.object.external_id] || [];

      // default to the original hover object
      let updatedSpatialObject = hoveredMarkerInfo.object;

      if (spatialObjectPositions.data) {
        // find the most recent position
        positionStorage.clear();
        positionStorage.load(spatialObjectPositions.data);
        const allPositions = positionStorage.all();
        const lastPosition = allPositions[allPositions.length - 1];

        // override the Point geometry with the latest position
        updatedSpatialObject = {
          ...hoveredMarkerInfo.object,
          shape: {
            ...hoveredMarkerInfo.object.shape,
            features: [
              {
                ...hoveredMarkerInfo.object.shape.features[0],
                geometry: lastPosition,
              },
            ],
          },
        };
      }

      dispatch(
        showPopover({
          spatialLayerKey: hoveredMarkerInfo.spatialLayerKey,
          spatialLayer: layerData,
          spatialObject: updatedSpatialObject,
        }),
      );
    }
  }, [dispatch, hoveredMarkerInfo, layerData, positions]);

  const handleMarkerClick = (info, spatialLayerKey) => {
    if (info?.object) {
      setActiveMarkerId(info?.object?.external_id);

      dispatch(
        setMapCardData({
          layerData,
          spatialObject: info?.object,
        }),
      );
      dispatch(setCardsToRender(AXON_MAP_CARD));

      trackMarkerClick(spatialLayerKey, {
        status: `${info?.object?.attributes?.device?.status}`.toLowerCase(),
      });
    }
  };

  const handleMarkerHover = (info, spatialLayerKey) => {
    if (info?.object) {
      overlay.setCursorPointer();
      setHoveredMarkerInfo({
        ...info,
        spatialLayerKey,
      });

      trackMarkerHover(LAYERS.AXON_BODY_3, {
        status: `${info?.object?.attributes?.device?.status}`.toLowerCase(),
      });
    } else {
      overlay.setCursorDefault();
      setHoveredMarkerInfo(null);
      dispatch(hidePopover());
    }
  };

  useAxonGL({
    activeMarkerId,
    overlay,
    onClick: handleMarkerClick,
    onHover: handleMarkerHover,
    positions: Object.values(positions),
    isMapIconDisabled,
  });

  return <></>;
});

export default useAxonGL;
